import { useEffect, useState } from "react";
import { BrandMark } from "@/components/BrandMark";
import { useAuth } from "@/lib/auth";
import { useBillingLifecycle } from "@/lib/billingLifecycle";
import "@/styles/secureWorkspaceEntry.css";

/** Presentation only: stages are supplied by existing trusted bootstrap results. */
export function SecureEntryScreen({ identity = false, company = false, verified = false, name, error, retry, paced = false, leaving = false, onExit }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    // Visibility debounce only. Never holds back the application when ready.
    const timer = setTimeout(() => setVisible(true), 120);
    return () => clearTimeout(timer);
  }, []);
  const [reveal, setReveal] = useState(0);
  useEffect(() => {
    if (!paced || leaving || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setReveal(4);
      return;
    }
    // Reveal genuine results progressively within the 2-second entry.
    // These timers do not run checks or turn unresolved checks into successes.
    const timers = [360, 840, 1400, 1700].map((delay, index) =>
      setTimeout(() => setReveal(index + 1), delay));
    return () => timers.forEach(clearTimeout);
  }, [paced, leaving]);
  const stages = [
    { label: "Identity", done: identity, active: !identity, detail: "Verifying your secure session…" },
    { label: "Company access", done: company, active: identity && !company, detail: "Confirming your company access…" },
    { label: "Workspace", done: verified, active: company && !verified, detail: "Securing your workspace…" },
  ];
  const current = stages.find(stage => stage.active);
  const complete = verified && (reveal >= 4 || leaving);
  const lastRevealed = stages.filter((stage, index) => stage.done && index < reveal).at(-1);
  return <div className={`fl-secure-entry ${leaving ? "fl-secure-entry-exit" : ""}`} onAnimationEnd={event => {
    if (event.target === event.currentTarget && leaving) onExit?.();
  }}>
    <div className={`fl-secure-content ${visible || error || leaving ? "fl-secure-visible" : ""}`} role="status" aria-live="polite" aria-atomic="true">
      <div className={`fl-secure-mark ${complete ? "fl-secure-verified" : ""}`}>
        <span className={`fl-secure-ring ${!complete && !error ? "fl-secure-working" : ""}`} aria-hidden="true" />
        <BrandMark size={72} />
      </div>
      <p className="font-display font-black text-2xl tracking-widest mt-5">FIELDLINK</p>
      <p className="text-xs tracking-[0.24em] text-slate-400 mt-1">SECURE WORKSPACE</p>
      <h1 className="font-bold text-base sm:text-lg tracking-wide mt-7">{error ? "WE COULDN’T VERIFY ACCESS" : complete ? "✓ SECURE ACCESS VERIFIED" : "FIELDLINK SECURE ACCESS"}</h1>
      <p className="text-sm text-slate-300 mt-2 min-h-5">{error ? "Please try again." : complete ? "WELCOME BACK" : current?.detail || (lastRevealed ? `${lastRevealed.label} verified` : "Opening Fieldlink…")}</p>
      {complete && name && <p className="font-display font-bold uppercase text-lg mt-1 break-words">{name}</p>}
      <ol className="fl-secure-stages" aria-label="Workspace access checks">
        {stages.map((stage, index) => <li key={stage.label} className={stage.done ? "fl-stage-done" : stage.active ? "fl-stage-active" : ""}>
          <span className="fl-stage-symbol" style={{ visibility: stage.done && index >= reveal ? "hidden" : "visible" }} aria-hidden="true">{stage.done ? "✓" : stage.active ? "●" : "○"}</span>
          <span><span className="block text-sm font-semibold">{stage.label}</span><span className="block text-xs text-slate-400 mt-0.5" style={{ visibility: stage.done && index >= reveal ? "hidden" : "visible" }}>{stage.done ? "Verified" : stage.active ? (error ? "Unresolved" : "Checking…") : "Pending"}</span></span>
        </li>)}
      </ol>
      {error && retry && <button className="mt-5 px-5 py-2 bg-[#FF5F15] text-white rounded-sm text-sm font-bold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4" onClick={retry}>Try again</button>}
    </div>
  </div>;
}

export default function SecureWorkspaceEntry({ children }) {
  const { user, operatingProfile, refresh: refreshAuth } = useAuth();
  const { data, error, refresh } = useBillingLifecycle();
  const [entered, setEntered] = useState(false);
  const [displayTimeElapsed, setDisplayTimeElapsed] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setDisplayTimeElapsed(true), 2000);
    return () => clearTimeout(timer);
  }, []);
  const [shown, setShown] = useState(false);
  const [exitDone, setExitDone] = useState(false);
  // Platform administrators retain their existing platform access/bypass.
  const bypass = !user || user.role === "super_admin";
  const identity = !!user;
  const company = !!(user?.role && user?.org_id && operatingProfile?.context?.org_id === user.org_id);
  const resolved = bypass || (company && !!data);
  const allowed = resolved && !data?.locked;
  const canEnter = resolved && (displayTimeElapsed || data?.locked || bypass);
  const failed = !resolved && (error || ["error", "unavailable"].includes(operatingProfile?.status));

  useEffect(() => {
    if (canEnter) {
      setEntered(true);
      if (data?.locked) setExitDone(true);
      return;
    }
    const timer = setTimeout(() => setShown(true), 120);
    return () => clearTimeout(timer);
  }, [canEnter, data?.locked]);
  const retry = () => {
    if (!company) refreshAuth();
    if (!data) refresh();
  };
  if (bypass) return children;
  if (!entered && !canEnter) return <SecureEntryScreen paced identity={identity} company={company} verified={allowed} name={data?.org_name} error={failed} retry={retry} />;
  // Enter after real access resolves and the requested minimum display time elapses.
  // Billing locks never claim workspace verification and go directly to the existing gate.
  return <>{children}{shown && allowed && !exitDone && <SecureEntryScreen identity company verified name={data?.org_name} leaving onExit={() => setExitDone(true)} />}</>;
}
